<script>
import ConfirmWithdrawApplicationModal from './ConfirmWithdrawApplicationModal.vue'
import moment from "moment";
import ProgressKnob from '../ProgressKnob.vue';

export default {
    components: {
        ConfirmWithdrawApplicationModal,
        ProgressKnob
    },
    props: {
        jobApplications: {
            type: Array,
            required: true,
        },
        csrfToken: {
            type: String,
            required: true
        },
        personalDetailPercentComplete: {
            type: Number,
            required: true
        }
    },

    data() {
        return {
            open: true,
            currentJobApplicationId: null,
            showAll: false
        }
    },

    computed: {
        displayedJobApplications() {
            return this.showAll ? this.jobApplications : this.jobApplications.slice(0, 5);
        }
    },

    methods: {
        withdrawApplication (jobApplicationId){
            this.currentJobApplicationId = jobApplicationId
            this.$refs.confirmWithdrawApplicationModal.toggleModal()
        },
        isRequisitionActive(closingDate) {
            // Parse the closing date using moment
            const closingDateMoment = moment(closingDate);

            // Get today's date
            const today = moment().startOf('day'); // This sets the time to the start of the day

            // Compare the closing date with today's date
            return closingDateMoment.isSameOrAfter(today);
        },
        toggleShowAll() {
            this.showAll = !this.showAll;
        }
    }
}
</script>

<template>
    <div class="job-application-accordion card">
        <div>
            <div class="grid-x grid-padding-y border-b">
                <div class="cell auto pl-20 pt-20 pb-10">
                    <span class="f-15 bold title">Your Job Applications &nbsp;<a v-if="this.jobApplications.length > 5" href="javascript:void(0)" @click="toggleShowAll">{{ showAll ? 'Show less' : 'See all' }}</a></span>
                </div>

                <div class="cell shrink pr-20 pt-20">
                    <a @click="open = !open">
                        <i class="fa" :class="open ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                    </a>
                </div>

                <div v-show="open" class="cell small-12 p-0">
                    <table class="table-job-applications show-for-large">
                        <thead>
                            <tr>
                                <th style="width: 30%"><div class="ml-20 mb-10 br-l-10">Job</div></th>
                                <th style="width: 10%"><div class="mb-10">Ref. No</div></th>
                                <th style="width: 32%"><div class="mb-10">Status</div></th>
                                <th style="width: 5%"><div class="mb-10">&nbsp;</div></th>
                                <th style="width: 10%"><div class="mr-20 br-r-10 mb-10">Action</div></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="jobApplication in displayedJobApplications" :key="jobApplication.id">
                                <td>
                                    <div class="grid-x grid-margin-x">
                                        <div class="cell shrink">
                                            <div v-if="jobApplication.application.job" class="ml-20">
                                                <img v-if="jobApplication.application.job && jobApplication.application.job.external_logo_path" :src="jobApplication.application.job.external_logo_path" alt="Company Logo" class="company-logo" />
                                                <img v-else src="/img/icons/merchant.png" alt="Merchant Logo" class="company-logo" />
                                            </div>
                                        </div>
                                        <div class="cell auto">
                                            <div v-if="jobApplication.application.job">
                                                <div class="job-title" v-if="jobApplication.application.job.requisition">
                                                    {{ jobApplication.application.job.requisition.display_title }}
                                                </div>
                                                <div class="job-title" v-else>
                                                    {{ jobApplication.application.job.title }}
                                                </div>
                                                <div class="job-location">
                                                    <img src="/img/icons/location_on.png" class="job-icons"/>
                                                    <span>{{ jobApplication.application.job.location }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span v-if="jobApplication.application.job">
                                        #{{ jobApplication.application.job.requisition_id }}
                                    </span>
                                </td>
                                <td>
                                    <template v-if="['failed', 'recycled'].includes(jobApplication.application.status)">
                                        <span class="job-application-status" :class="{'text-danger': ['failed'].includes(jobApplication.application.status)}">
                                            <strong v-if="jobApplication.application.status">
                                                Unsuccessful -
                                            </strong>
                                        </span>
                                        <span v-if="jobApplication.application.fail_reason">
                                            {{ jobApplication.application.fail_reason }}
                                        </span>
                                    </template>
                                    <template v-else>
                                        <!-- <div v-if="jobApplication.stepToFinishMessage !==''" v-html="jobApplication.stepToFinishMessage"></div> -->
                                        <div v-if="jobApplication.statusTitle === 'Incomplete'" class="text-warning">{{ jobApplication.statusTitle }} <span v-if="jobApplication.stepToFinishMessage" class="text-black">- {{jobApplication.stepToFinishMessage }}</span></div>
                                        <div v-if="jobApplication.statusTitle === 'Pending'">{{ jobApplication.statusTitle }}</div>
                                    </template>
                                </td>
                                <td>
                                    <ProgressKnob class="knob" v-if="jobApplication.statusTitle === 'Incomplete'" :currentStep="jobApplication.application.completed_step" :totalSteps="jobApplication.application.total_step" />
                                </td>
                                <td>
                                    <div class="action-buttons">
                                        <a
                                            v-if="!['failed', 'recycled'].includes(jobApplication.application.status)"
                                            :href="jobApplication.stepLink ? jobApplication.stepLink : `/candidate/job-applications/view/${jobApplication.application.id}`"
                                            class="btn btn-hover-gray"
                                        >
                                            Continue&nbsp;<i class="fa-solid fa-chevron-right"></i>
                                        </a>
                                        <a
                                            v-if="!['failed', 'recycled'].includes(jobApplication.application.status)"
                                            class="btn text-danger ml-10 btn-hover-red"
                                            @click="withdrawApplication(jobApplication.application.id)"
                                        >
                                            Cancel
                                        </a>
                                        <a
                                            v-if="['failed'].includes(jobApplication.application.status) && jobApplication.application.is_reappliable && isRequisitionActive(jobApplication.application.job.requisition.closing_date)"
                                            :href="`/candidate/job-applications/reapply/${jobApplication.application.id}`"
                                            class="btn btn-reapply ml-10 btn-hover-gray"
                                        >
                                            Reapply
                                        </a>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div class="hide-for-large mt-10 p-20">
                        <div v-for="jobApplication in jobApplications" :key="jobApplication.id" class="grid-x grid-padding-y border-b">
                            <div class="cell small-2">
                                <div v-if="jobApplication.application.job">
                                    <img v-if="jobApplication.application.job && jobApplication.application.job.external_logo_path" :src="jobApplication.application.job.external_logo_path" alt="Company Logo" class="company-logo" />
                                    <img v-else src="/img/icons/merchant.png" alt="Merchant Logo" class="company-logo" />
                                </div>
                            </div>
                            <div class="cell small-10">
                                <div v-if="jobApplication.application.job">
                                    <div class="job-title" v-if="jobApplication.application.job.requisition">
                                        {{ jobApplication.application.job.requisition.display_title }}
                                    </div>
                                    <div class="job-title" v-else>
                                        {{ jobApplication.application.job.title }}
                                    </div>
                                    <div class="job-location">
                                        <img src="/img/icons/location_on.png" class="job-icons"/>
                                        <span>{{ jobApplication.application.job.location }}</span>
                                    </div>
                                </div>
                                <div v-if="jobApplication.application.job">
                                    #{{ jobApplication.application.job.requisition_id }}
                                </div>
                                <div class="grid-x grid-padding-x">
                                    <div class="cell small-12">
                                        <template v-if="['failed', 'recycled'].includes(jobApplication.application.status)">
                                            <span class="job-application-status" :class="{'text-danger': ['failed'].includes(jobApplication.application.status)}">
                                                <strong v-if="jobApplication.application.status">
                                                    Unsuccessful -
                                                </strong>
                                            </span>
                                            <span v-if="jobApplication.application.fail_reason">
                                                {{ jobApplication.application.fail_reason }}
                                            </span>
                                        </template>
                                        <template v-else>
                                            <!-- <div v-if="jobApplication.stepToFinishMessage !==''" v-html="jobApplication.stepToFinishMessage"></div> -->
                                            <div v-if="jobApplication.statusTitle === 'Incomplete'" class="text-warning">{{ jobApplication.statusTitle }}</div>
                                            <div v-if="jobApplication.statusTitle === 'Pending'">{{ jobApplication.statusTitle }}</div>
                                        </template>
                                    </div>
                                </div>
                                <div class="grid-x grid-padding-x mt-20">
                                    <div class="cell small-2" v-if="jobApplication.statusTitle === 'Incomplete'">
                                        <ProgressKnob class="knob" :currentStep="jobApplication.application.completed_step" :totalSteps="jobApplication.application.total_step" />
                                    </div>
                                    <div class="cell" :class="{'small-10': jobApplication.statusTitle === 'Incomplete'}">
                                        <div class="action-buttons mt-0">
                                            <a
                                                v-if="!['failed', 'recycled'].includes(jobApplication.application.status)"
                                                :href="`/candidate/job-applications/view/${jobApplication.application.id}`"
                                                class="btn btn-hover-gray ml-10"
                                            >
                                                Continue&nbsp;<i class="fa-solid fa-chevron-right"></i>
                                            </a>
                                            <a
                                                v-if="!['failed', 'recycled'].includes(jobApplication.application.status)"
                                                class="btn text-danger ml-10 btn-hover-red"
                                                @click="withdrawApplication(jobApplication.application.id)"
                                            >
                                                Cancel
                                            </a>

                                            <a
                                                v-if="['failed'].includes(jobApplication.application.status) && jobApplication.application.is_reappliable && isRequisitionActive(jobApplication.application.job.requisition.closing_date)"
                                                :href="`/candidate/job-applications/reapply/${jobApplication.application.id}`"
                                                class="btn btn-reapply ml-10 btn-hover-gray"
                                            >
                                                Reapply
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="cell small-12">
                                <hr class="mobile-divider mt-5 mb-5"/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <confirm-withdraw-application-modal
            :job-application-id="currentJobApplicationId"
            :csrf-token="csrfToken"
            ref="confirmWithdrawApplicationModal"
        />
    </div>
</template>

<style scoped>
.mobile-divider {
    border: none;
    height: 4px;
    background-color: grey;
    margin: 0 -20px;
    width: calc(100% + 40px);
}

@media screen and (max-width: 39.9375em) {
    .job-title {
        font-size: 14px;
        line-height: 1.2;
        margin-bottom: 2px;
    }
    
    .job-location {
        font-size: 12px;
        margin-bottom: 2px;
    }

    .job-info-right {
        display: flex;
        align-items: center;
        font-size: 12px;
        line-height: 1.2;
    }

    .job-info-right strong {
        font-size: 12px;
        display: block;
        margin-top: 2px;
    }

    .job-icons {
        width: 12px;
        height: 12px;
    }
}
</style>